import * as Sentry from '@sentry/react';

import LogRocket from 'logrocket';

// use display names in case we want to print it
const ENV_PROD = 'Production';
const ENV_DEMO = 'Demo';
const ENV_SANDBOX = 'Sandbox';
const ENV_UAT = 'UAT';
const ENV_DEV_PREFIX = 'DEV ';
const ENV_DEV_REGEX = new RegExp(`^${ENV_DEV_PREFIX}`);
const ENV_DEV_1 = ENV_DEV_PREFIX + '1';
const ENV_DEV_2 = ENV_DEV_PREFIX + '2';
const ENV_DEV_3 = ENV_DEV_PREFIX + '3';
const ENV_DEV_4 = ENV_DEV_PREFIX + '4';
const ENV_DEV_5 = ENV_DEV_PREFIX + '5';

// The deployed environment comes straight from the backend's ENVIRONMENT var
// (injected as REACT_APP_ENVIRONMENT), so every environment sets it and the
// frontend never has to infer it from the URL. Keys match the backend's ENV_*
// values exactly. `local` is intentionally absent: it is the only non-deployed
// environment and is handled separately below (per-port DEV labels).
const ENVIRONMENT_TO_DISPLAY_NAME: Record<string, string> = {
  prod: ENV_PROD,
  uat: ENV_UAT,
  demo: ENV_DEMO,
  sbox: ENV_SANDBOX,
};

// Local development runs several instances in parallel on different ports; label
// them DEV 1..5 by their app URL so they're distinguishable in the UI. This only
// applies when REACT_APP_ENVIRONMENT is `local`.
const LOCAL_URL_TO_DISPLAY_NAME: Record<string, string> = {
  'http://localhost:3000': ENV_DEV_1,
  'http://host.docker.internal:3000': ENV_DEV_1,
  'http://localhost:3100': ENV_DEV_2,
  'http://host.docker.internal:3100': ENV_DEV_2,
  'http://localhost:3200': ENV_DEV_3,
  'http://host.docker.internal:3200': ENV_DEV_3,
  'http://localhost:3300': ENV_DEV_4,
  'http://host.docker.internal:3300': ENV_DEV_4,
  'http://localhost:3400': ENV_DEV_5,
  'http://host.docker.internal:3400': ENV_DEV_5,
};

const getEnvironmentName = () => {
  const environment = process.env.REACT_APP_ENVIRONMENT;
  if (!environment) {
    throw new Error(
      'Missing required environment variable: REACT_APP_ENVIRONMENT'
    );
  }

  const displayName = ENVIRONMENT_TO_DISPLAY_NAME[environment];
  if (displayName) {
    return displayName;
  }

  if (environment === 'local') {
    const url = process.env.REACT_APP_CONFIRM_APP_URL ?? '';
    if (!LOCAL_URL_TO_DISPLAY_NAME[url]) {
      console.error(
        'config.js: unrecognized local REACT_APP_CONFIRM_APP_URL: ' +
          url +
          ', defaulting to DEV UNKNOWN'
      );
    }
    return LOCAL_URL_TO_DISPLAY_NAME[url] ?? ENV_DEV_PREFIX + 'UNKNOWN';
  }

  throw new Error('Unrecognized REACT_APP_ENVIRONMENT: ' + environment);
};

const config = {
  init: () => {
    // throw error for any unrecognized hostnames
    if (
      !(
        window.location.hostname.endsWith('.ngrok.io') ||
        window.location.hostname.endsWith('.ngrok-free.app') ||
        window.location.hostname.endsWith('.ngrok-free.dev') ||
        window.location.hostname.endsWith('.confirm.com') ||
        window.location.hostname.endsWith('.confirmhr.com') ||
        window.location.hostname === 'confirm.learningpool.com'
      ) &&
      window.location.hostname !== 'localhost'
    ) {
      throw new Error('Invalid hostname: ' + window.location.hostname);
    }

    const environmentName = getEnvironmentName();
    // Only initialize Sentry when a DSN is configured. This lets us disable
    // Sentry per-environment (e.g. data residency reasons) by leaving the env
    // var empty without breaking app boot.
    if (
      !ENV_DEV_REGEX.test(environmentName) &&
      process.env.REACT_APP_SENTRY_DSN
    ) {
      Sentry.init({
        dsn: process.env.REACT_APP_SENTRY_DSN,
        environment: environmentName,
        release: process.env.REACT_APP_SENTRY_RELEASE,
        // Tag events with the full environment vocabulary. The `environment`
        // field above is the display name (e.g. "Production"); these tags carry
        // the raw injected values so a dedicated instance (e.g. LP, deploy id
        // "lp-prod") is distinguishable from the primary stacks that share the
        // same environment. `environment` is set as a tag too, for symmetry.
        initialScope: {
          tags: {
            environment: process.env.REACT_APP_ENVIRONMENT ?? '',
            deploy_id: process.env.REACT_APP_DEPLOY_ID ?? '',
            instance: process.env.REACT_APP_INSTANCE ?? '',
          },
        },
        integrations: [
          // Automatically instrument Node.js libraries and frameworks
          Sentry.browserTracingIntegration(),
          Sentry.captureConsoleIntegration({
            levels: ['error'],
          }),
        ],
        tracesSampleRate: environmentName === 'prod' ? 0.1 : 1.0,
        ignoreErrors: [
          // ignore these per https://forum.sentry.io/t/resizeobserver-loop-limit-exceeded/8402/5
          'ResizeObserver loop limit exceeded',
          // ignore these per https://github.com/getsentry/sentry-javascript/issues/3440#issuecomment-954602174
          'Non-Error promise rejection captured with value',
          // ignore these that are triggered when we are in maintenance window
          '"maintenance"',
          // LogRocket's own adoptedStyleSheets instrumentation conflicting
          // with another script/extension on the page; thrown entirely
          // inside the LogRocket SDK, nothing actionable on our side
          // (CONFIRM-APP-38V).
          'Cannot redefine property: adoptedStyleSheets',
          // Browser-extension noise, not an app error: a content script's
          // runtime.sendMessage() call failing because its tab was closed.
          // Already known noise in isWhiteListedErrorMessage(), but that only
          // covers it as an API-call failure message, and it also reaches
          // Sentry as a global error: https://confirm.sentry.io/issues/7454511925/
          'Invalid call to runtime.sendMessage(). Tab not found.',
        ],
        beforeSend: (event) => {
          // see https://docs.logrocket.com/docs/sentry
          if (LogRocket.sessionURL) {
            if (!event.extra) {
              event.extra = {};
            }
            event.extra.sessionURL = LogRocket.sessionURL;
          }
          return event;
        },
      });
    }

    // Polyfill Intl.Locale if not available (required by intl-relativetimeformat)
    if (!Intl.Locale) {
      require('@formatjs/intl-locale/polyfill.js');
    }

    // for "Last Updated" widgets to work on iPhone and test environments
    if (!Intl.RelativeTimeFormat) {
      require('@formatjs/intl-relativetimeformat/polyfill');
      require('@formatjs/intl-relativetimeformat/locale-data/en');
    }

    if (ENV_DEV_REGEX.test(environmentName)) {
      // UNCOMMENT HERE TO ENABLE MSW
      // ON LOCAL DEVELOPMENT
      // (add new handlers to mocks/browser.ts)
      // const { worker } = require('mocks/browser');
      // worker.start();
    }

    // ensure environment variables that are needed exist
    if (!process.env.REACT_APP_CONFIRM_APP_URL) {
      throw new Error(
        'Missing required environment variable: REACT_APP_CONFIRM_APP_URL'
      );
    }
    if (!process.env.REACT_APP_CONFIRM_ADMIN_URL) {
      throw new Error(
        'Missing required environment variable: REACT_APP_CONFIRM_ADMIN_URL'
      );
    }
    if (!process.env.REACT_APP_CONFIRM_API_URL) {
      throw new Error(
        'Missing required environment variable: REACT_APP_CONFIRM_API_URL'
      );
    }
    if (!process.env.REACT_APP_AUTH_DOMAIN) {
      throw new Error(
        'Missing required environment variable: REACT_APP_AUTH_DOMAIN'
      );
    }
    if (!process.env.REACT_APP_AUTH_AUDIENCE) {
      throw new Error(
        'Missing required environment variable: REACT_APP_AUTH_AUDIENCE'
      );
    }
    if (!process.env.REACT_APP_AUTH0_CLIENT_ID) {
      throw new Error(
        'Missing required environment variable: REACT_APP_AUTH0_CLIENT_ID'
      );
    }
  },
};

// @ts-expect-error
config.getEnvironmentName = getEnvironmentName;

// @ts-expect-error
config.isProduction = () => {
  return getEnvironmentName() === ENV_PROD;
};

// @ts-expect-error
config.isUat = () => {
  return getEnvironmentName() === ENV_UAT;
};

// @ts-expect-error
config.isLocal = () => {
  return ENV_DEV_REGEX.test(getEnvironmentName());
};

// @ts-expect-error
config.isDemo = () => {
  return getEnvironmentName() === ENV_DEMO;
};

// @ts-expect-error
config.isSandbox = () => {
  return getEnvironmentName() === ENV_SANDBOX;
};

// Intercom is only loaded in production, and only when an app id is
// configured (it is left empty for regions without Intercom, e.g. lp-prod).
// Anything that opens the messenger should be hidden when this is false.
// @ts-expect-error
config.isIntercomEnabled = () => {
  // @ts-expect-error
  return config.isProduction() && !!process.env.REACT_APP_INTERCOM_APP_ID;
};

// @ts-expect-error
config.getOauthConnection = () => {
  // Commented out when we added Microsoft.
  // This would force google auth independently of how many
  // Social Logins are active on Auth0.
  // if (config.isProduction()) {
  //   return 'google-oauth2';
  // }

  // Returning undefined will show the login selector for the user to
  // pick the identity service they want. To disable the form email/password
  // delete the Database integration on Auth0.
  // (https://community.auth0.com/t/social-only-login/46277)
  return undefined;
};

// for event tracking (note: we use staging to ensure the calls don't break anything before pushing to production)
// @ts-expect-error
config.getTracking = () => {
  // @ts-expect-error
  const isProduction = config.isProduction();
  // @ts-expect-error
  const isLocal = config.isLocal();

  // Each integration is only "on" when it is both enabled for the environment
  // AND its config is present. This keeps every downstream usage site (which
  // gates on these flags) in sync with whether the integration was actually
  // initialized — an integration left unconfigured (e.g. disabled for a region
  // via an empty env var) is never called into.
  return {
    useGoogleAnalyticsTracking:
      !isLocal && !!process.env.REACT_APP_GA_MEASUREMENT_ID,
    useLogRocketEventTracking:
      !isLocal && !!process.env.REACT_APP_LOGROCKET_APP_ID,
    useConsoleEventTracking: !isProduction,
  };
};

export default config;
